<template>
    <div class="sellsy-password-input">
        <CompassInput
            v-model="currentValue"
            :type="type"
            :readonly="readonly"
            :placeholder="placeholder"
            :size="size"
            class="sellsy-password-input__password"
            @focus="
                showBadges = true;
                dirty = true;
            "
            @blur="showBadges = false"
            @change="dirty = true"
        >
            <template slot="suffix">
                <CompassIconButton
                    size="small"
                    :icon="showPassword ? 'eyehide-fill' : 'eyeshow-fill'"
                    @click="toggleShow"
                />
            </template>
        </CompassInput>
        <transition-group
            v-if="showPasswordStrength"
            class="password__badges"
            tag="div"
            name="slide-in"
        >
            <TagThird
                :key="0"
                :style="{ '--i': 0 }"
                :class="hasCorrectLength ? 'password__badges-item--valid' : 'password__badges-item'"
                type="custom"
            >
                <span class="password__badges-item-content typographies-paragraph-medium-regular">
                    <CompassIcon
                        v-show="hasCorrectLength"
                        type="primary-contrast"
                        size="small"
                        icon="check"
                    />
                    {{
                        $t.__('inputs.PasswordWithBadgeInput.between_min_and_max_characters', {
                            min: minLength,
                            max: maxLength,
                        })
                    }}
                </span>
            </TagThird>
            <TagThird
                :key="1"
                :style="{ '--i': 1 }"
                :class="
                    hasUppercase && hasLowerCase
                        ? 'password__badges-item--valid'
                        : 'password__badges-item'
                "
                type="custom"
            >
                <span class="password__badges-item-content typographies-paragraph-medium-regular">
                    <CompassIcon
                        v-show="hasUppercase && hasLowerCase"
                        type="primary-contrast"
                        size="small"
                        icon="check"
                    />
                    {{ $t.__('inputs.PasswordWithBadgeInput.one_upper_case_and_one_lower_case') }}
                </span>
            </TagThird>
            <TagThird
                :key="2"
                :style="{ '--i': 2 }"
                :class="hasNumber ? 'password__badges-item--valid' : 'password__badges-item'"
                type="custom"
            >
                <span class="password__badges-item-content typographies-paragraph-medium-regular">
                    <CompassIcon
                        v-show="hasNumber"
                        type="primary-contrast"
                        size="small"
                        icon="check"
                    />
                    {{ $t.__('inputs.PasswordWithBadgeInput.one_number') }}
                </span>
            </TagThird>
        </transition-group>
    </div>
</template>

<script>
import { minLengthPassword, maxLengthPassword } from '../forms/rules/Rules';
import TagThird from 'vcomponents/common/TagThird/TagThird.vue';

export default {
    components: {
        TagThird,
    },
    props: {
        readonly: {
            type: Boolean,
            required: false,
            default: false,
        },
        value: {
            type: [Number, String, Boolean],
            required: false,
            default: '',
        },
        placeholder: {
            type: String,
            required: false,
            default: '',
        },
        size: {
            type: String,
            default: 'default',
        },
    },
    data() {
        return {
            currentValue: this.value === null ? '' : this.value.replace(/<br\s*[\/]?>/gi, '\n'),
            showPassword: false,
            showBadges: false,
            dirty: false,
            minLength: minLengthPassword(),
            maxLength: maxLengthPassword(),
        };
    },
    computed: {
        hasCorrectLength() {
            return (
                this.currentValue.length >= this.minLength &&
                this.currentValue.length <= this.maxLength
            );
        },
        hasMinLength() {
            return this.currentValue.length >= this.minLength;
        },
        hasNumber() {
            return /\d/.test(this.currentValue);
        },
        hasUppercase() {
            return /[A-Z]/.test(this.currentValue);
        },
        hasLowerCase() {
            return /[a-z]/.test(this.currentValue);
        },
        isValid() {
            return (
                this.hasMinLength &&
                this.hasMaxLength &&
                this.hasNumber &&
                this.hasLowercase &&
                this.hasUppercase
            );
        },
        suffixIcon() {
            return !this.showPassword ? 'icon-eye-fillout' : 'icon-eye-no-fillout';
        },
        type() {
            return this.showPassword ? 'text' : 'password';
        },
        showPasswordStrength() {
            return this.showBadges || (!this.isValid && this.dirty);
        },
    },
    watch: {
        currentValue() {
            this.$emit('input', this.currentValue);
            this.$emit('isValid', this.isValid);
        },
        value(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.currentValue = newValue;
            }
        },
    },
    created() {
        this.$emit('isValid', this.isValid);
    },
    methods: {
        toggleShow() {
            this.showPassword = !this.showPassword;
        },
    },
};
</script>

<style lang="scss">
.slide-in {
    &-move {
        transition: opacity 0.3s linear, transform 0.1s ease-in-out;
        transition-property: none;
    }

    &-leave-active {
        transition: opacity 0.4s linear, transform 0.4s ease;
        transition-delay: calc(0.05s * (var(--total) - var(--i)));
    }

    &-enter-active {
        transition: opacity 0.5s linear, transform 0.5s ease;
        transition-delay: calc(0.05s * var(--i));
    }

    &-enter,
    &-leave-to {
        opacity: 0;
    }

    &-enter {
        transform: translateX(-1em);
    }
    &-leave-to {
        transform: translateX(1em);
    }
}

.sellsy-password-input {
    --total: 3;

    &__password {
        width: 100%;
    }

    input[type='password'].el-input__inner {
        &::placeholder {
            color: var(--colors-common-text-standard-placeholder) !important;
        }
    }
    .el-input__suffix {
        right: var(--spacing-1);
        display: flex;
        align-items: center;

        .el-input__suffix-inner {
            display: flex;
            width: 14px;
        }
    }

    .password__badges {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-1);
        margin: var(--spacing-1) 0 0;

        &-item {
            background-color: var(--colors-component-background-actionable-default);
            border-color: var(--colors-component-border-actionable-default);
            color: var(--colors-common-text-standard-default);

            &-content {
                display: flex;
                align-items: center;
                gap: var(--spacing-1);
                color: var(--colors-common-text-standard-default);
            }

            &--valid {
                background-color: var(--colors-common-background-success-darker);
                color: var(--colors-component-text-actionable-active);

                .password__badges-item-content {
                    color: var(--colors-common-text-contrast-default);
                }
            }
        }
    }
}
</style>
